import Popup from 'reactjs-popup';
import { useState, useMemo, useEffect } from 'react';
import { Input, TimePicker, Select, Button } from '../Common';
import ModeConfigurationForm from '../../containers/ModeScheduler/ModeConfigurationForm';
import { getFieldMeta, isMode, showComment, calculateEndTime } from '../../utils/modeScheduleUtils';
import InfoTooltip from '../InfoTooltip/InfoTooltip';

const EMPTY_INITIAL_DATA = {
    startDate: 'Monday',
    startTime: null,
    duration: { hours: 0, minutes: 0 },
    mode: 'idle',
    recurrence: 'once',
    config: {},
    configId: null,
};

const ModeSchedulerModal = ({ open, setOpen, modes, weekDays, initialData, onSave, onEdit, onDelete, isDailyBp }) => {
    const [data, setData] = useState(initialData);

    useEffect(() => {
        setData(initialData || EMPTY_INITIAL_DATA);
    }, [initialData]);

    const parseTimeToDate = timeStr => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const now = new Date();
        now.setHours(hours);
        now.setMinutes(minutes);
        now.setSeconds(0);
        now.setMilliseconds(0);
        return now;
    };

    const sanitizeConfigValues = (config, modeName) => {
        const sanitized = {};
        const meta = getFieldMeta(modeName);

        for (const [key, value] of Object.entries(config)) {
            const fieldMeta = meta[key] || {};
            if (value === '' && fieldMeta?.min !== undefined) {
                sanitized[key] = null;
            } else if (typeof value === 'string' && fieldMeta?.min !== undefined) {
                sanitized[key] = isNaN(value) ? value : parseFloat(value);
            } else {
                sanitized[key] = value;
            }
        }

        if (isMode(modeName?.toLowerCase(), 'direct_command') && typeof config.intervals === 'string') {
            try {
                sanitized.intervals = JSON.parse(config.intervals);
            } catch (e) {
                console.warn('Failed to parse intervals JSON', e);
                sanitized.intervals = [];
            }
        }

        return sanitized;
    };

    const handleDurationChange = (unit, rawValue) => {
        const value = parseInt(rawValue, 10);
        setData(prev => ({
            ...prev,
            duration: { ...prev.duration, [unit]: isNaN(value) ? '' : value },
        }));
    };

    const handleDurationBlur = unit => {
        setData(prev => {
            let value = parseInt(prev.duration[unit], 10);
            value = isNaN(value) ? 0 : value;

            if (unit === 'minutes') {
                const allowed = [0, 15, 30, 45];
                value = allowed.reduce((prevVal, currVal) => (Math.abs(currVal - value) < Math.abs(prevVal - value) ? currVal : prevVal));
            } else {
                value = Math.max(0, Math.min(value, 24));
            }

            return {
                ...prev,
                duration: { ...prev.duration, [unit]: value },
            };
        });
    };

    const isEdit = initialData?.mode !== 'idle';

    const dayOptions = useMemo(
        () =>
            weekDays.map(date => ({
                value: date.toFormat('cccc'),
                label: date.toFormat('cccc'),
            })),
        [weekDays]
    );

    const selectedMode = modes.find(m => m.name === data?.mode);
    const requiredFields = selectedMode?.config_schema?.required || [];

    const durationInSeconds = data?.duration.hours * 3600 + data?.duration.minutes * 60;

    const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    const isDirty = !deepEqual(data, initialData);

    const handleSaveMode = e => {
        const newMode = e.target.value;
        const selected = modes.find(m => m.name === newMode);
        const defaults = {};

        if (selected?.config_schema?.properties) {
            for (const key in selected.config_schema.properties) {
                if (key === 'intervals' && isMode(selected.name.toLowerCase(), 'direct_command')) {
                    defaults[key] = '[]';
                } else {
                    defaults[key] = selected.config_schema.properties[key].default || '';
                }
            }
        }

        setData(prev => ({
            ...prev,
            mode: newMode,
            config: defaults,
        }));
    };

    const handleClose = () => {
        setOpen(false);
        setData(EMPTY_INITIAL_DATA);
    };

    const handleSave = async () => {
        if (!isFormValid && !isDirty) {
            return;
        }

        if (onSave) {
            onSave({ ...data, config: sanitizeConfigValues(data.config, data.mode) });
        }
        handleClose();
    };

    const handleEdit = () => {
        if (!isFormValid && !isDirty) {
            return;
        }

        if (onEdit) {
            onEdit({ ...data, config: sanitizeConfigValues(data.config, data.mode) }, initialData);
        }
        handleClose();
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete(initialData);
        }
        handleClose();
    };

    const validateConfigValues = (config, modeName, requiredFields) => {
        if (isMode(modeName?.toLowerCase(), 'direct_command')) {
            return true;
        }

        if (isMode(modeName?.toLowerCase(), 'target_soc')) {
            const lower = parseFloat(config.lower_deadband);
            const upper = parseFloat(config.upper_deadband);
            if (!isNaN(lower) && !isNaN(upper) && lower > upper) {
                return false;
            }
        }

        const meta = getFieldMeta(modeName);
        if (!meta) {
            return false;
        }

        for (const key of requiredFields) {
            const val = config[key];
            if (val === undefined || (typeof val === 'string' && val.trim() === '')) {
                return false;
            }

            const fieldMeta = meta[key];
            if (fieldMeta) {
                const numeric = parseFloat(val);
                if (isNaN(numeric)) {
                    return false;
                }
                if (fieldMeta.min !== undefined && numeric < fieldMeta.min) {
                    return false;
                }
                if (fieldMeta.max !== undefined && numeric > fieldMeta.max) {
                    return false;
                }
            }
        }

        return true;
    };

    const isFormValid =
        data?.mode !== 'idle' &&
        !!data?.startDate &&
        data?.startTime !== null &&
        durationInSeconds > 0 &&
        (requiredFields.length === 0 || validateConfigValues(data?.config, data?.mode, requiredFields));

    const timeRange = { startTime: data?.startTime, endTime: calculateEndTime(data?.startTime, data?.duration) };

    const timeTooltipText =
        'Time must be in 15-minute intervals: 00, 15, 30, 45 minutes past the hour. Other values will be rounded to the nearest valid time.';

    return (
        <Popup open={open} onClose={handleClose} modal className="modal mode-scheduler-modal">
            <div className="mode-scheduler-modal__header">
                Schedule
                <button className="mode-scheduler-modal__close" onClick={handleClose}>
                    &#10005;
                </button>
            </div>

            <div className="mode-scheduler-modal__content">
                <div className="mode-scheduler-modal__form-group">
                    <label className="mode-scheduler-modal__label">Start date:</label>
                    <div className="mode-scheduler-modal__datetime">
                        <Select
                            options={isDailyBp ? [{ value: data?.startDate ?? 'recurring', label: 'Recurring' }] : dayOptions}
                            value={data?.startDate}
                            onChange={e => setData({ ...data, startDate: e.target.value })}
                            className="mode-scheduler-modal__input"
                            disabled={isEdit || isDailyBp}
                        />
                        <TimePicker
                            value={data?.startTime ? parseTimeToDate(data.startTime) : null}
                            onChange={date => {
                                const hours = date.getHours().toString().padStart(2, '0');
                                const minutes = date.getMinutes().toString().padStart(2, '0');
                                setData({ ...data, startTime: `${hours}:${minutes}` });
                            }}
                            className="mode-scheduler-modal__input"
                        />
                    </div>
                </div>

                <div className="mode-scheduler-modal__form-group">
                    <label className="mode-scheduler-modal__label">Duration:</label>
                    <div className="mode-scheduler-modal__duration">
                        {['hours', 'minutes'].map(unit => (
                            <div key={unit} className="mode-scheduler-modal__duration-box">
                                <Input
                                    type="number"
                                    value={data?.duration[unit]}
                                    onChange={e => handleDurationChange(unit, e.target.value)}
                                    onBlur={() => handleDurationBlur(unit)}
                                    className="mode-scheduler-modal__input-small"
                                    min={0}
                                    max={unit === 'hours' ? 24 : 59}
                                />
                                {unit.charAt(0).toUpperCase() + unit.slice(1)}
                            </div>
                        ))}
                    </div>
                    <InfoTooltip tooltip={timeTooltipText} />
                </div>

                <div className="mode-scheduler-modal__form-group">
                    <label className="mode-scheduler-modal__label">Mode:</label>
                    <Select
                        options={[
                            { value: 'idle', label: 'Select mode' },
                            ...modes
                                .filter(m => m.name.toLowerCase() !== 'idle')
                                .map(mode => ({
                                    value: mode.name,
                                    label: mode.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                                })),
                        ]}
                        value={data?.mode}
                        onChange={e => handleSaveMode(e)}
                        className="mode-scheduler-modal__input"
                    />
                    <InfoTooltip tooltip={showComment(data?.mode)} />
                </div>

                <ModeConfigurationForm
                    configValues={data?.config}
                    setConfigValues={updater => {
                        setData(prev => ({
                            ...prev,
                            config: typeof updater === 'function' ? updater(prev.config) : updater,
                        }));
                    }}
                    selectedMode={selectedMode}
                    requiredFields={requiredFields}
                    data={data}
                    timeRange={timeRange}
                />

                {!isEdit && !isDailyBp && (
                    <div className="mode-scheduler-modal__form-group">
                        <label className="mode-scheduler-modal__label">Recurrence type:</label>
                        <Select
                            options={[
                                { value: 'once', label: 'Once' },
                                { value: 'every_day', label: 'Every day' },
                            ]}
                            value={data?.recurrence}
                            onChange={e => setData({ ...data, recurrence: e.target.value })}
                            className="mode-scheduler-modal__input"
                        />
                    </div>
                )}
            </div>

            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                <span style={{ color: 'red' }}>*</span> Required fields must be filled before saving.
            </p>

            <div className="mode-scheduler-modal__footer">
                <Button className="modal__cancel" text="Cancel" onClick={handleClose} />
                <Button text="Save" onClick={isEdit ? handleEdit : handleSave} disabled={!isFormValid || (isEdit && !isDirty)} />
                {isEdit && <Button text="Delete" onClick={handleDelete} />}
            </div>
        </Popup>
    );
};

export default ModeSchedulerModal;
